import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];
const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const EVENT_COLOR = {
  birthday: 'bg-pink-500',
  project_deadline: 'bg-red-500',
  project_created: 'bg-green-500',
  demo_deadline: 'bg-orange-500',
  invoice_pending: 'bg-yellow-500',
};
const EVENT_LABEL = {
  birthday: 'Aniversário',
  project_deadline: 'Entrega',
  project_created: 'Criação',
  demo_deadline: 'Demo',
  invoice_pending: 'Retake',
};

const getEventColor = (type) => EVENT_COLOR[type] || 'bg-blue-500';
const getEventTypeLabel = (type) => EVENT_LABEL[type] || 'Evento';

const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

export const CalendarModal = ({
  open,
  onClose,
  calendarEvents,
  calMonth,
  calYear,
  setCalMonth,
  setCalYear,
}) => {
  if (!open) return null;

  const getEventsForDay = (day) => {
    const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarEvents.filter(e => e.date === dateStr);
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const today = new Date();
  const isToday = (day) =>
    day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 rounded-xl p-6 w-full max-w-3xl border border-slate-700 my-auto max-h-[calc(100vh-2rem)] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="calendar-modal"
      >
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="h-6 w-6 text-purple-400" />
          Calendário de Eventos
        </h2>

        {/* Month navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={prevMonth}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
            data-testid="cal-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-xl font-semibold text-white">{MONTHS[calMonth]} {calYear}</h3>
          <button
            onClick={nextMonth}
            className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white transition-colors"
            data-testid="cal-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEK_DAYS.map(d => (
            <div key={d} className="text-center text-xs font-medium text-slate-400 py-2">{d}</div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: getFirstDayOfMonth(calMonth, calYear) }, (_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          {Array.from({ length: getDaysInMonth(calMonth, calYear) }, (_, i) => {
            const day = i + 1;
            const events = getEventsForDay(day);
            const hasEvents = events.length > 0;
            return (
              <div key={day} className="relative group" data-testid={`cal-day-${day}`}>
                <div className={`aspect-square flex flex-col items-center justify-center rounded-lg text-sm transition-all cursor-default
                  ${isToday(day) ? 'ring-2 ring-teal-400 bg-teal-500/10 text-teal-400 font-bold' :
                    hasEvents ? 'bg-slate-700/80 text-white font-medium' : 'bg-slate-800/30 text-slate-400'}
                  ${hasEvents ? 'hover:bg-slate-600' : ''}
                `}>
                  <span>{day}</span>
                  {hasEvents && (
                    <div className="flex gap-0.5 mt-0.5">
                      {events.slice(0, 3).map((e, idx) => (
                        <div
                          key={`dot-${day}-${e.type}-${e.title}-${idx}`}
                          className={`w-1.5 h-1.5 rounded-full ${getEventColor(e.type)}`}
                        />
                      ))}
                      {events.length > 3 && (
                        <span className="text-[8px] text-slate-400">+{events.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
                {hasEvents && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-900 border border-slate-600 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                    <p className="text-xs text-slate-400 mb-2 font-medium">{day} de {MONTHS[calMonth]}</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {events.map((e, idx) => (
                        <div key={`evt-${day}-${e.type}-${e.title}-${idx}`} className="flex items-start gap-2">
                          <div className={`w-2 h-2 rounded-full mt-1 shrink-0 ${getEventColor(e.type)}`} />
                          <div>
                            <span className="text-[10px] text-slate-400">{getEventTypeLabel(e.type)}</span>
                            <p className="text-xs text-white">{e.title}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-600" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-700 flex-wrap">
          {['birthday', 'project_deadline', 'demo_deadline', 'project_created', 'invoice_pending'].map(type => (
            <div key={type} className="flex items-center gap-1.5 text-xs text-slate-400">
              <div className={`w-2.5 h-2.5 rounded-full ${getEventColor(type)}`} />
              {getEventTypeLabel(type)}
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition-colors"
        >
          Fechar
        </button>
      </div>
    </div>
  );
};
